import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CameraForm, type CameraDraft } from '../components/CameraForm';
import {
  IKON_AKSI,
  ITEM_MENU,
  MenuIkon,
  TautanIkon,
  TombolIkon,
} from '../components/IconAction';
import { CameraThumb } from '../components/CameraThumb';
import { DialogHapus, type KonfirmasiHapus } from '../components/DialogHapus';
import { FullscreenViewer } from '../components/FullscreenViewer';
import { canManagePrivate, isAdmin, isStaff, useAuth } from '../hooks/useAuth';
import {
  api,
  hapusTerkonfirmasi,
  listTags,
  listTemplates,
  setCameraMaintenance,
  type Camera,
  type CameraTagWithCount,
  type CameraTemplate,
  type TestResult,
} from '../lib/api';
import { TagChips, TagFilter, TagManager } from '../components/CameraTags';
import { CameraBulkBar } from '../components/CameraBulkBar';
import { ReplaceCameraDialog } from '../components/ReplaceCameraDialog';
import { Pager, usePaging } from '../components/Pager';
import { cocokKamera } from '../lib/cariKamera';
import { useBahasa } from '../i18n';

/** §G3.1 — status tidak pernah hanya lewat warna, selalu ada label teks. */
function StatusBadge({ camera }: { camera: Camera }) {
  const { t } = useBahasa();
  const map = {
    online: { dot: 'bg-green-600', label: 'Online' },
    offline: { dot: 'bg-[var(--color-text-muted)]', label: 'Offline' },
    error: { dot: 'bg-amber-500', label: 'Error' },
  } as const;
  // Pemeliharaan menang atas status apa pun (§G3.1), dan memakai biru brand —
  // satu-satunya warna palet yang belum dipakai sebagai status.
  const s = camera.maintenanceSince
    ? { dot: 'bg-accent', label: 'Maintenance' }
    : map[camera.status];
  return (
    <span className="flex items-center gap-2">
      <span aria-hidden className={`inline-block h-2.5 w-2.5 rounded-full ${s.dot}`} />
      <span>{t(s.label)}</span>
      {!camera.isEnabled && <span className="text-xs text-muted">{t('(disabled)')}</span>}
    </span>
  );
}

const Gembok = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

/** Tombol sekunder kartu kamera — satu sumber, supaya barisnya rata. */
const TOMBOL =
  'inline-flex h-9 items-center rounded-md border border-line px-2.5 text-xs transition-[background-color,scale] duration-150 hover:bg-subtle active:scale-[0.98]';

/**
 * Ukuran kartu, pola Windows Explorer.
 *
 * `List` mempertahankan kartu teks yang ada sebelum thumbnail masuk — pada 96
 * kamera itu tetap wujud tercepat untuk mencari satu nama, dan ia satu-satunya
 * wujud yang tidak menarik gambar sama sekali. Tiga sisanya menukar kepadatan
 * dengan gambar.
 *
 * `perPage` naik bersama kekecilan kartu supaya tinggi halaman kira-kira tetap:
 * 48 kartu kecil dan 12 kartu besar sama-sama sekitar enam baris.
 *
 * **`List` berubah bentuk 8 September 2026.** Sampai hari itu ia kartu penuh
 * dalam dua kolom, 12 per halaman — jadi "daftar" yang tidak bisa dipakai untuk
 * hal yang paling dasar dari sebuah daftar. Dilaporkan lapangan: *"gw mau liat
 * daftar nama kamera seluruhnya gak bisa soalnya"*, dan permintaannya menyusul
 * di kalimat sebelumnya: *"di mode daftar, ada yang dropdown ke bawah dong"*.
 *
 * Sekarang `List` = satu baris per kamera, seluruh armada dalam satu halaman,
 * dan keterangannya turun ke bawah baris itu saat dibuka. Satu kolom dan bukan
 * dua: yang dikerjakan orang di sini memindai nama dari atas ke bawah, dan mata
 * yang harus melompat balik ke puncak kolom kedua kehilangan tempatnya —
 * ditambah panel yang terbuka di dalam grid dua kolom akan mendorong
 * tetangganya ikut memanjang.
 */
const UKURAN = {
  list: { label: 'List', kolom: 'grid-cols-1', perPage: 100, thumb: null },
  small: { label: 'Small', kolom: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6', perPage: 48, thumb: 'aspect-video' },
  medium: { label: 'Medium', kolom: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4', perPage: 24, thumb: 'aspect-video' },
  large: { label: 'Large', kolom: 'grid-cols-1 sm:grid-cols-2', perPage: 12, thumb: 'aspect-video' },
} as const;

type NamaUkuran = keyof typeof UKURAN;

const URUTAN = {
  'name-asc': { label: 'Name A→Z', cmp: (a: Camera, b: Camera) => bandingNama(a, b) },
  'name-desc': { label: 'Name Z→A', cmp: (a: Camera, b: Camera) => bandingNama(b, a) },
  status: {
    label: 'Problems first',
    cmp: (a: Camera, b: Camera) => peringkatStatus(a) - peringkatStatus(b) || bandingNama(a, b),
  },
} as const;

type NamaUrutan = keyof typeof URUTAN;

/**
 * Urutan nama memakai pembanding lokal dengan `numeric`, bukan `<` biasa.
 *
 * Nama kamera di armada ini berpola "Gate 2", "Gate 10", "Gate 11". Perbandingan
 * string apa adanya menaruh 10 dan 11 sebelum 2, dan daftar yang berurut menurut
 * mesin tapi tidak menurut manusia lebih menyesatkan daripada daftar acak.
 */
function bandingNama(a: Camera, b: Camera): number {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

/** error → offline → online. Yang perlu ditengok naik ke atas. */
function peringkatStatus(c: Camera): number {
  if (c.status === 'error') return 0;
  if (c.status === 'offline') return 1;
  return 2;
}

/** Pilihan tampilan menempel ke browser, bukan ke akun — ini selera per layar. */
const SIMPAN_UKURAN = 'lensa.cameras.size';
const SIMPAN_URUTAN = 'lensa.cameras.sort';

function bacaPilihan<T extends string>(kunci: string, sah: readonly T[], bawaan: T): T {
  try {
    const v = localStorage.getItem(kunci);
    if (v && (sah as readonly string[]).includes(v)) return v as T;
  } catch {
    /* penyimpanan diblokir — pakai bawaan */
  }
  return bawaan;
}

/** Bentuk minimum dashboard rakitan yang dibutuhkan halaman ini (§F9). */
interface MonitorRingkas {
  id: string;
  name: string;
  cameraIds: string[];
}

export function CamerasPage() {
  const { t } = useBahasa();
  const { user } = useAuth();
  /**
   * Dua tingkat, bukan satu (§F8). `staf` membuka yang bisa dibatalkan —
   * tambah, sunting, uji koneksi; `admin` membuka yang tidak — hapus kamera
   * beserta folder rekamannya. Keduanya sudah memperhitungkan User view
   * (§F8.1), jadi tombolnya ikut hilang di mode itu.
   */
  const staf = isStaff(user);
  const admin = isAdmin(user);
  /** Pengelola kamera privat: melihat kamera privat orang lain (tanpa gambar). */
  const kelola = canManagePrivate(user);
  /** Kamera privat milik orang lain — gambar & rekamannya tidak boleh diminta sama sekali. */
  const terkunci = (c: Camera) =>
    c.private !== null && !c.private.owners.some((o) => o.id === user?.id);
  const [orang, setOrang] = useState<{ id: string; username: string; fullName: string; isActive: boolean }[]>([]);

  const [cameras, setCameras] = useState<Camera[]>([]);
  /** Umur bawaan §D6, dipakai form untuk menjelaskan kolom retensi yang kosong. */
  const [retensiGlobal, setRetensiGlobal] = useState(7);
  /** §D2 — panjang segmen bawaan; dipakai baris daftar dan form kamera. */
  const [segmenGlobal, setSegmenGlobal] = useState(900);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Camera | null | 'new'>(null);
  const [busy, setBusy] = useState(false);
  /** Tanda "Tersimpan." sesudah form ditutup, hilang sendiri — bukti simpannya berhasil. */
  const [tersimpan, setTersimpan] = useState(false);
  useEffect(() => {
    if (editing) setTersimpan(false);
    if (!tersimpan) return;
    const h = window.setTimeout(() => setTersimpan(false), 4000);
    return () => window.clearTimeout(h);
  }, [tersimpan, editing]);
  const [tests, setTests] = useState<Record<string, TestResult | 'running'>>({});
  const [cari, setCari] = useState('');
  /** Kamera yang sedang ditonton layar penuh dari halaman ini (§G11). */
  const [layarPenuh, setLayarPenuh] = useState<Camera | null>(null);
  /** Kamera yang sedang dikonfirmasi penghapusannya (§G11). */
  const [akanDihapus, setAkanDihapus] = useState<Camera | null>(null);
  /** Dialog Replace camera (§D10.2) — kamera yang perangkatnya sedang diganti. */
  const [akanDiganti, setAkanDiganti] = useState<Camera | null>(null);
  /**
   * Panel aksi yang sedang terbuka — satu untuk seluruh halaman, bukan satu per
   * kartu. Pada 96 kamera, panel yang menyimpan keadaannya sendiri berarti 96
   * panel yang bisa terbuka bersamaan; yang dilihat orang setelah menelusuri
   * beberapa kartu adalah selusin panel menggantung yang harus ditutup satu per
   * satu.
   */
  const [panel, setPanel] = useState<{ id: string; jenis: 'more' | 'dash' } | null>(null);
  /**
   * Dashboard rakitan milik sendiri (§F9), untuk ikon "Add to dashboard".
   *
   * Diminta lapangan: *"Dashboard list bisa gak sih kalo add cameranya langsung
   * aja dari Cameras, terus ada icon 'add to dashboard'? Jadi gak usah ada 2
   * halaman yang sebenernya bisa diwakilin dalam 1 halaman?"* Halaman Custom
   * Dashboards tetap ada — di sanalah tempat menamai, mengurutkan, dan
   * menghapus — tapi pekerjaan yang paling sering dilakukan, yaitu memasukkan
   * satu kamera yang barusan ditemukan, sekarang bisa dari tempat menemukannya.
   *
   * Dimuat sekali bersama daftar kamera; isinya kecil (segelintir baris) dan
   * memuatnya per kartu berarti 96 permintaan untuk satu daftar yang sama.
   */
  const [tags, setTags] = useState<CameraTagWithCount[]>([]);
  const [templates, setTemplates] = useState<CameraTemplate[]>([]);
  /**
   * Kamera terpilih untuk operasi massal (§G11.2).
   *
   * Disimpan sebagai himpunan id apa adanya, lalu **disaring terhadap yang
   * sedang terlihat** saat dipakai (`terpilihAktif` di bawah). Dengan begitu
   * mengubah saringan tidak pernah menyisakan kamera terpilih yang tidak ada di
   * layar — dan tindakan massal tidak akan pernah mengenai kamera yang orangnya
   * sudah tidak lihat sejak beberapa klik lalu.
   */
  const [terpilih, setTerpilih] = useState<Set<string>>(new Set());
  const [kelolaTag, setKelolaTag] = useState(false);
  /**
   * Tag terpilih hidup di **URL**, bukan di state saja (§G11.1).
   *
   * Daftar tersaring yang tidak bisa ditautkan berarti "kirim saya daftar
   * kamera critical" dijawab dengan tangkapan layar. Ia juga bertahan menyeberangi
   * muat ulang, yang justru sering terjadi di halaman ini — orang membuka web UI
   * kamera di tab lain lalu kembali.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const tagTerpilih = useMemo(() => searchParams.getAll('tag'), [searchParams]);
  const [dashboards, setDashboards] = useState<MonitorRingkas[]>([]);
  const [dashBusy, setDashBusy] = useState(false);
  const [ukuran, setUkuran] = useState<NamaUkuran>(() =>
    bacaPilihan(SIMPAN_UKURAN, Object.keys(UKURAN) as NamaUkuran[], 'medium'),
  );
  const [urutan, setUrutan] = useState<NamaUrutan>(() =>
    bacaPilihan(SIMPAN_URUTAN, Object.keys(URUTAN) as NamaUrutan[], 'name-asc'),
  );

  useEffect(() => {
    try {
      localStorage.setItem(SIMPAN_UKURAN, ukuran);
      localStorage.setItem(SIMPAN_URUTAN, urutan);
    } catch {
      /* penyimpanan diblokir — pilihan tetap berlaku untuk sesi ini */
    }
  }, [ukuran, urutan]);

  const tutupPanel = useCallback(() => setPanel(null), []);

  function togglePanel(id: string, jenis: 'more' | 'dash') {
    setPanel((p) => (p?.id === id && p.jenis === jenis ? null : { id, jenis }));
  }

  const muatDashboards = useCallback(async () => {
    try {
      const r = await api<{ monitors: MonitorRingkas[] }>('/monitors');
      setDashboards(r.monitors);
    } catch {
      // Gagal memuat daftar ini tidak boleh merusak halaman Cameras: yang
      // hilang cuma satu ikon, dan halamannya masih punya tujuh aksi lain.
    }
  }, []);

  useEffect(() => {
    void muatDashboards();
  }, [muatDashboards]);

  /**
   * Saklar keanggotaan satu kamera pada satu dashboard rakitan.
   *
   * Memanggil endpoint delta (`POST/DELETE .../cameras`), bukan `PUT` yang
   * mengirim seluruh isi monitor: dua tab yang menambah kamera berbeda pada
   * dashboard yang sama akan saling menimpa kalau lewat `PUT`.
   */
  async function toggleDiDashboard(monitor: MonitorRingkas, cameraId: string) {
    if (dashBusy) return;
    const sudah = monitor.cameraIds.includes(cameraId);
    setDashBusy(true);
    try {
      const r = await api<{ monitor: MonitorRingkas }>(
        sudah ? `/monitors/${monitor.id}/cameras/${cameraId}` : `/monitors/${monitor.id}/cameras`,
        sudah ? { method: 'DELETE' } : { method: 'POST', body: JSON.stringify({ cameraId }) },
      );
      setDashboards((ds) => ds.map((d) => (d.id === r.monitor.id ? r.monitor : d)));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDashBusy(false);
    }
  }

  /** Bikin dashboard baru langsung berisi kamera ini. */
  async function dashboardBaru(camera: Camera) {
    if (dashBusy) return;
    const nama = prompt(t('New dashboard containing {nama}:', { nama: camera.name }), camera.name);
    if (nama === null || !nama.trim()) return;
    setDashBusy(true);
    try {
      const r = await api<{ monitor: MonitorRingkas }>('/monitors', {
        method: 'POST',
        body: JSON.stringify({ name: nama.trim(), cameraIds: [camera.id] }),
      });
      setDashboards((ds) => [...ds, r.monitor].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDashBusy(false);
    }
  }

  /**
   * Saringan tag dikerjakan **server**, bukan `Array.filter` di sini.
   *
   * Pencarian teks memang disaring di klien — daftarnya sudah ada di memori dan
   * mengetiknya harus terasa seketika. Tag berbeda: ia dipakai sebagai jalan
   * masuk ke operasi massal nanti (§G11.2), dan "pilih semua yang cocok
   * saringan sekarang" hanya bisa dipercaya kalau yang menentukan cocok atau
   * tidak adalah satu tempat, yaitu query yang sama dengan yang mengirim
   * daftarnya.
   */
  const load = useCallback(async () => {
    try {
      const qs = [
        ...(kelola ? ['kelola=1'] : []),
        ...tagTerpilih.map((n) => `tag=${encodeURIComponent(n)}`),
      ].join('&');
      const [r, daftarTag, daftarTemplate, daftarOrang] = await Promise.all([
        api<{
          cameras: Camera[];
          retentionDefaultDays: number;
          segmentDefaultSeconds: number;
        }>(`/cameras${qs ? `?${qs}` : ''}`),
        listTags(),
        // Viewer tidak punya endpoint ini (§G11.4 — staf saja) dan juga tidak
        // punya tombol "Add camera", jadi daftarnya memang boleh kosong. Yang
        // tidak boleh: satu 403 di sini menjatuhkan seluruh halaman kamera.
        staf ? listTemplates().catch(() => []) : Promise.resolve([]),
        kelola
          ? api<{ users: typeof orang }>('/users').then((u) => u.users)
          : Promise.resolve([]),
      ]);
      setOrang(daftarOrang);
      setCameras(r.cameras);
      setRetensiGlobal(r.retentionDefaultDays);
      setSegmenGlobal(r.segmentDefaultSeconds);
      setTags(daftarTag);
      setTemplates(daftarTemplate);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [tagTerpilih, staf, kelola]);

  useEffect(() => {
    void load();
  }, [load]);

  // Disaring dulu baru diurutkan: mengurutkan 96 baris lalu membuang 90 di
  // antaranya adalah kerja yang dibayar tiap ketukan tombol di kotak pencarian.
  const terlihat = useMemo(() => {
    const q = cari.trim();
    return cameras.filter((c) => cocokKamera(c, q)).sort(URUTAN[urutan].cmp);
  }, [cameras, cari, urutan]);

  // Irisan "dipilih" ∩ "terlihat". Bukan `terpilih` mentah — lihat alasannya
  // di deklarasi state-nya.
  const terpilihAktif = useMemo(
    () => terlihat.filter((c) => terpilih.has(c.id)).map((c) => c.id),
    [terlihat, terpilih],
  );

  /**
   * Baris daftar yang sedang terbuka (§G11, 8 Sep 2026).
   *
   * `Set`, bukan satu id: membandingkan dua kamera berarti membuka keduanya dan
   * melihatnya bersebelahan, dan akordeon yang menutup yang satu saat yang lain
   * dibuka melarang justru perbandingan itu.
   *
   * Tidak ikut disimpan ke localStorage: yang dibuka orang tergantung apa yang
   * sedang dikerjakannya menit ini, bukan selera yang menetap seperti ukuran
   * kartu.
   */
  const [barisTerbuka, setBarisTerbuka] = useState<Set<string>>(() => new Set());

  const paging = usePaging(terlihat, UKURAN[ukuran].perPage);

  async function save(draft: CameraDraft, pemilik?: string[]) {
    setBusy(true);
    setError(null);
    try {
      const isNew = editing === 'new';
      await api(isNew ? '/cameras' : `/cameras/${(editing as Camera).id}`, {
        method: isNew ? 'POST' : 'PUT',
        body: JSON.stringify(draft),
      });
      // Pemilik kamera privat lewat endpoint-nya sendiri (§F8.2), sesudah
      // config tersimpan — gagal di sini tetap meninggalkan form terbuka.
      if (!isNew && pemilik !== undefined) {
        await api(`/cameras/${(editing as Camera).id}/private`, {
          method: 'PUT',
          body: JSON.stringify({ ownerUserIds: pemilik }),
        });
      }
      setEditing(null);
      setTersimpan(true);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(camera: Camera, konfirmasi: KonfirmasiHapus) {
    // Galatnya sengaja dilempar tanpa `setError`: banner halaman ada di
    // belakang overlay dialog, jadi "password salah" yang ditulis ke sana tidak
    // terlihat sama sekali oleh orang yang sedang menatap dialognya.
    // `DialogHapus` yang menampilkannya, di tempat orangnya sedang melihat.
    await hapusTerkonfirmasi(`/cameras/${camera.id}`, konfirmasi);
    setAkanDihapus(null);
    await load();
  }

  async function test(camera: Camera, profile: 'main' | 'sub') {
    setTests((t) => ({ ...t, [camera.id]: 'running' }));
    try {
      const result = await api<TestResult>(`/cameras/${camera.id}/test?profile=${profile}`, {
        method: 'POST',
      });
      setTests((t) => ({ ...t, [camera.id]: result }));
    } catch (err) {
      setTests((t) => ({
        ...t,
        [camera.id]: { ok: false, profile, url: '', message: (err as Error).message },
      }));
    }
  }

  if (loading) return <p className="p-8 text-sm text-muted">Loading cameras…</p>;

  if (editing) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <CameraForm
          camera={editing === 'new' ? null : editing}
          busy={busy}
          retentionDefaultDays={retensiGlobal}
          segmentDefaultSeconds={segmenGlobal}
          tags={tags}
          templates={templates}
          kelolaPrivat={
            kelola && editing !== 'new'
              ? {
                  // Pemilik yang sudah nonaktif tetap ikut, supaya chip-nya terbaca.
                  orang: orang.filter(
                    (u) => u.isActive || !!editing.private?.owners.some((o) => o.id === u.id),
                  ),
                }
              : undefined
          }
          error={error}
          onSubmit={(draft, pemilik) => void save(draft, pemilik)}
          onCancel={() => {
            setEditing(null);
            setError(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-xl">{t('Cameras')}</h1>

        {/* Pencarian menggantikan `<select>` "Show" yang dulu ada di sini. Yang
            lama menyaring ke tepat satu kamera lewat daftar 96 baris — untuk
            menemukan satu nama, mengetiknya selalu lebih cepat daripada
            menggulung daftar itu, dan pencarian juga menjangkau host dan path.

            Kendali ini tetap dirender meski daftarnya pendek: bilah atas yang
            berpindah tempat setiap kali kamera ditambah lebih mengganggu
            daripada satu kotak yang sesekali tidak terpakai. */}
        <label className="flex items-center gap-2 text-sm">
          <span className="sr-only">{t('Search cameras')}</span>
          <input
            type="search"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
            /* Viewer tidak menerima `host` (§F6), jadi menjanjikannya di
             placeholder berarti menyuruh orang mencari sesuatu yang memang
             tidak akan pernah ketemu. */
          placeholder={staf ? t('Search Name, Host or Path') : t('Search Name or Path')}
            className="min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm sm:w-64"
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted">{t('Sort')}</span>
          <select
            value={urutan}
            onChange={(e) => setUrutan(e.target.value as NamaUrutan)}
            className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm"
          >
            {(Object.keys(URUTAN) as NamaUrutan[]).map((k) => (
              <option key={k} value={k}>
                {t(URUTAN[k].label)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted">{t('Size')}</span>
          <select
            value={ukuran}
            onChange={(e) => setUkuran(e.target.value as NamaUkuran)}
            className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm"
          >
            {(Object.keys(UKURAN) as NamaUkuran[]).map((k) => (
              <option key={k} value={k}>
                {t(UKURAN[k].label)}
              </option>
            ))}
          </select>
        </label>

        <TagFilter
          tags={tags}
          terpilih={tagTerpilih}
          onChange={(names) => {
            // Saringan yang berubah selalu mengembalikan ke halaman pertama
            // secara tidak langsung: `load()` mengganti daftarnya, dan `usePaging`
            // menjepit halaman ke jumlah yang baru.
            const p = new URLSearchParams(searchParams);
            p.delete('tag');
            for (const n of names) p.append('tag', n);
            setSearchParams(p, { replace: true });
          }}
        />

        {staf && (
          <button
            type="button"
            onClick={() => setKelolaTag(true)}
            className="min-h-tap rounded-md border border-line px-3 text-sm"
          >
            {t('Manage tags')}
          </button>
        )}

        {staf && (
          <Link
            to="/cameras/import"
            className="flex min-h-tap items-center rounded-md border border-line px-3 text-sm"
          >
            {t('Import')}
          </Link>
        )}

        {staf && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast"
          >
            {t('Add camera')}
          </button>
        )}
      </div>

      {akanDiganti && (
        <ReplaceCameraDialog
          camera={akanDiganti}
          onSelesai={load}
          onTutup={() => setAkanDiganti(null)}
        />
      )}

      {kelolaTag && (
        <TagManager
          tags={tags}
          onClose={() => setKelolaTag(false)}
          onChanged={load}
        />
      )}

      {error && (
        <p role="alert" className="mt-4 rounded-md border border-red-600 px-3 py-2 text-sm">
          {error}
        </p>
      )}
      {tersimpan && (
        <p role="status" className="mt-4 rounded-md border border-line px-3 py-2 text-sm">
          {t('Saved.')}
        </p>
      )}

      {/* Tiga keadaan kosong, bukan satu (§G11). Saringan tag dikerjakan server,
          jadi `cameras` yang kosong bisa berarti "belum ada kamera" ATAU "tidak
          ada yang punya semua tag ini" — dan kalimat "Add the first one" untuk
          keadaan kedua membuat orang mengira 96 kameranya hilang. */}
      {cameras.length === 0 && tagTerpilih.length > 0 ? (
        <p className="mt-8 text-sm text-muted">
          {t('No camera has all of these tags: {daftar}.', { daftar: tagTerpilih.join(', ') })}{' '}
          <button
            type="button"
            onClick={() => {
              const p = new URLSearchParams(searchParams);
              p.delete('tag');
              setSearchParams(p, { replace: true });
            }}
            className="underline underline-offset-2"
          >
            {t('Clear tag filter')}
          </button>
        </p>
      ) : cameras.length === 0 ? (
        <p className="mt-8 text-sm text-muted">
          {staf
            ? t('No cameras yet. Add the first one.')
            : t('No cameras have been assigned to your account yet.')}
        </p>
      ) : terlihat.length === 0 ? (
        // Dibedakan dari "belum ada kamera" di atas: kalimat yang sama untuk
        // kedua keadaan membuat orang mengira kameranya hilang, padahal yang
        // terjadi cuma pencariannya tidak cocok.
        <p className="mt-8 text-sm text-muted">
          {t('No camera matches “{cari}”.', { cari: cari.trim() })}{' '}
          <button type="button" onClick={() => setCari('')} className="underline underline-offset-2">
            {t('Clear search')}
          </button>
        </p>
      ) : (
        <ul className={`mt-6 grid gap-3 ${UKURAN[ukuran].kolom}`}>
          {paging.slice.map((c) => {
            const uji = tests[c.id];
            /**
             * Mode daftar padat: satu baris, keterangannya turun saat dibuka.
             *
             * `rinci` yang menentukan seluruh isi di bawah, dan di ukuran kartu
             * mana pun selain `list` ia selalu `true` — jadi kartu grid tidak
             * berubah sama sekali oleh perubahan ini.
             */
            const padat = ukuran === 'list';
            const rinci = !padat || barisTerbuka.has(c.id);
            return (
              <li
                key={c.id}
                className={`flex flex-col rounded-lg border border-line bg-surface ${
                  padat ? 'px-2 py-1.5' : 'p-3'
                }`}
              >
                {UKURAN[ukuran].thumb &&
                  (terkunci(c) ? (
                    // Kotak putih seukuran thumbnail, tanpa satu pun request gambar.
                    <div
                      className={`mb-2 flex items-center justify-center gap-1.5 rounded-md border border-line bg-white text-sm text-neutral-600 ${UKURAN[ukuran].thumb}`}
                    >
                      <Gembok />
                      {t('Private')}
                    </div>
                  ) : (
                    <CameraThumb cameraId={c.id} className={`mb-2 ${UKURAN[ukuran].thumb}`} />
                  ))}

                <div className="flex items-start justify-between gap-2">
                  {staf && (
                    <label className="flex min-h-tap min-w-tap shrink-0 items-center">
                      <span className="sr-only">{t('Select {name}', { name: c.name })}</span>
                      <input
                        type="checkbox"
                        checked={terpilih.has(c.id)}
                        onChange={(e) =>
                          setTerpilih((s) => {
                            const next = new Set(s);
                            if (e.target.checked) next.add(c.id);
                            else next.delete(c.id);
                            return next;
                          })
                        }
                      />
                    </label>
                  )}
                  {/* Di mode daftar namanya sendiri yang jadi tombol buka:
                      sasaran seluruh lebar baris, bukan satu panah 9px di
                      ujung. Judulnya tetap `h2` — yang berubah cuma isinya jadi
                      bisa ditekan, bukan tingkatannya di pohon dokumen. */}
                  <h2 className="min-w-0 flex-1 text-base">
                    {padat ? (
                      <button
                        type="button"
                        onClick={() =>
                          setBarisTerbuka((s) => {
                            const next = new Set(s);
                            if (next.has(c.id)) next.delete(c.id);
                            else next.add(c.id);
                            return next;
                          })
                        }
                        aria-expanded={rinci}
                        className="flex min-h-tap w-full min-w-0 items-center gap-2 text-left"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className={`h-4 w-4 shrink-0 text-muted transition-transform duration-150 ${
                            rinci ? 'rotate-90' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m9 6 6 6-6 6" />
                        </svg>
                        <span className="truncate" title={c.name}>
                          {c.name}
                        </span>
                        {c.private && !rinci && <Gembok className="h-3.5 w-3.5 shrink-0 text-muted" />}
                        {/* Catatan ikut di baris yang tertutup, dipotong satu
                            baris (§G11). Inilah yang diminta: *"jadi di mode
                            list/daftar, remarksnya bisa keliatan"* — kalau ia
                            cuma muncul sesudah dibuka, membaca catatan 97
                            kamera berarti membuka 97 baris. Disembunyikan di
                            layar sempit: di sana nama kameranya sendiri sudah
                            berebut tempat. */}
                        {c.remarks && !rinci && (
                          <span
                            className="hidden min-w-0 flex-[2] truncate text-xs font-normal text-muted md:block"
                            title={c.remarks}
                          >
                            {c.remarks}
                          </span>
                        )}
                      </button>
                    ) : (
                      <span className="block truncate" title={c.name}>
                        {c.name}
                      </span>
                    )}
                  </h2>
                  <div className="shrink-0 self-center text-xs">
                    <StatusBadge camera={c} />
                  </div>
                </div>

                {/* Chip tag duduk tepat di bawah nama (§G11.1). Pada kartu
                    `Small` selebar ±96px ia jadi titik berwarna: tiga kata
                    tidak muat tanpa mengecilkan nama kameranya, dan nama
                    kamera yang menang. */}
                {rinci && c.private && (
                  <p className="mt-1 inline-flex w-fit max-w-full items-center gap-1 rounded-full border border-line bg-subtle px-2 py-0.5 text-xs">
                    <Gembok className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {t('Private · {nama}', { nama: c.private.owners.map((o) => o.name).join(', ') })}
                    </span>
                  </p>
                )}
                {rinci && c.tags.length > 0 && (
                  <div className="mt-1">
                    <TagChips tags={c.tags} compact={ukuran === 'small'} />
                  </div>
                )}

                {/* Catatan bebas (§G11, 8 Sep 2026), utuh dan dengan barisnya
                    dipertahankan — di baris daftar yang tertutup ia sudah
                    tampil dipotong satu baris di sebelah nama.

                    `whitespace-pre-wrap`: yang ditulis orang di sini sering
                    berupa daftar pendek, dan meratakannya jadi satu paragraf
                    membuang satu-satunya struktur yang dia beri. Di kartu grid
                    dipotong tiga baris — kartu yang tingginya ditentukan
                    panjang catatan satu kamera merusak jajaran seluruh
                    halaman. */}
                {rinci && c.remarks && (
                  <p
                    className={`mt-1.5 whitespace-pre-wrap rounded-md bg-subtle px-2 py-1 text-xs ${
                      padat ? '' : 'line-clamp-3'
                    }`}
                    title={c.remarks}
                  >
                    {c.remarks}
                  </p>
                )}

                {/* URL selalu tersamar — password kamera tidak pernah dikirim
                    ke klien (§F5). Satu baris: alamatnya yang dicari orang,
                    bukan seluruh query string.

                    **Viewer tidak menerimanya sama sekali** (§F6, 1 Sep 2026):
                    server mengirim `null`, jadi baris ini hilang dengan
                    sendirinya. Syarat `staf` di depannya bukan pagar — pagarnya
                    di server — melainkan supaya kartunya tidak menyisakan baris
                    kosong setinggi satu baris di 96 kartu sekaligus. */}
                {rinci && staf && c.mainUrlMasked && (
                  <p className="mt-1 truncate font-mono text-xs text-muted" title={c.mainUrlMasked}>
                    {c.mainUrlMasked}
                  </p>
                )}

                {/* Nama folder rekamannya di NAS (§B4). Sejak 7 September 2026
                    ia acak dan tidak menyebut nama kamera — bagus untuk siapa
                    pun yang membuka NAS-nya, tapi berarti tidak ada lagi cara
                    menebak folder mana milik kamera mana. Barisnya ini yang
                    menggantikan tebakan itu, dan ia satu-satunya arah yang
                    disediakan: dari kamera ke folder, bukan sebaliknya.

                    Hanya pada ukuran kartu selain `small`: pada kartu ±96px,
                    baris ini akan mendorong keluar hal-hal yang dicari orang
                    setiap hari demi keterangan yang dicari beberapa kali
                    setahun. */}
                {rinci && staf && c.folderName && ukuran !== 'small' && (
                  <p className="mt-0.5 truncate font-mono text-[11px] text-muted" title={t('Recording folder')}>
                    {c.folderName}
                  </p>
                )}

                {rinci && (
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted">
                  <span>{c.transport.toUpperCase()}</span>
                  <span>{c.streamFps} fps</span>
                  {/* Mode kamera (§D2) ditulis di depan retensi, dan "View only"
                      menggantikan angka retensi alih-alih berdiri di sebelahnya:
                      "View only · 7d" akan terbaca seperti janji bahwa ada tujuh
                      hari rekaman yang bisa dibuka, padahal tidak ada satu pun. */}
                  {c.recordEnabled ? (
                    <>
                      {/* Kamera yang ikut global ditandai, bukan ditulis
                          angkanya begitu saja (§D6): "7d" pada kamera yang
                          mewarisi dan "7d" pada kamera yang menyetel sendiri
                          adalah dua keadaan berbeda, dan yang pertama berubah
                          sendiri kalau angka globalnya diubah. */}
                      <span
                        title={
                          c.maxDay === null ? t('Follows the global retention setting') : undefined
                        }
                      >
                        {c.maxDay === null
                          ? t('{n}d global', { n: retensiGlobal })
                          : `${c.maxDay}d`}
                      </span>
                      <span>
                        {c.segmentSeconds === null
                          ? t('{n}m global', { n: Math.round(segmenGlobal / 60) })
                          : `${Math.round(c.segmentSeconds / 60)}m`}
                      </span>
                    </>
                  ) : (
                    <span className="font-medium">{t('View only')}</span>
                  )}
                </div>
                )}

                {rinci && c.lastError && (
                  <p className="mt-1.5 line-clamp-2 text-xs text-amber-500" title={c.lastError}>
                    {c.lastError}
                  </p>
                )}

                {/* Jalur tercepat saat ada kamera bermasalah — dan alasan utama
                    halaman /logs dibuat (§G9). Tersedia untuk semua peran:
                    isinya yang difilter per assignment, bukan aksesnya (§D7.3). */}
                {/* Satu baris aksi, bukan dua. "View logs" sempat berdiri
                    sendiri di baris atas dan terbaca seperti tombol utama
                    padahal setara dengan yang lain. `Delete` didorong ke ujung:
                    aksi merusak tidak boleh bersebelahan dengan `Edit`. */}
                {/* Aksi berbentuk ikon, keterangannya menyusul saat disentuh
                    penunjuk atau difokus papan ketik (§G11).

                    Yang digantikan delapan tombol teks yang berjajar di satu
                    baris. Pada kartu `Medium` — ukuran bawaan — baris itu
                    selalu patah jadi dua atau tiga, sehingga tinggi tiap kartu
                    ditentukan panjang kata "Camera settings" dan bukan oleh
                    isinya. Ikon menyusut ke satu baris di semua ukuran kartu.

                    Yang tersisa terbuka cuma tiga aksi yang paling sering
                    dipakai. Empat sisanya — dua uji koneksi, Edit, web UI
                    kamera, hapus — pindah ke dalam dua panel: aksi yang dipakai
                    sekali sebulan tidak perlu memakan lebar yang sama dengan
                    aksi yang dipakai tiap hari.

                    Hapus khususnya: sebelumnya ia berdiri langsung di sebelah
                    Edit, dan di dalam menu ia butuh satu langkah lagi sebelum
                    tersentuh — pagar yang murah untuk aksi yang membuang folder
                    rekaman. */}
                {rinci && (
                <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-3">
                  {!terkunci(c) && (
                    <>
                      <TombolIkon label={t('Live view')} nama="live" onClick={() => setLayarPenuh(c)} />
                      <TautanIkon label={t('Playback')} nama="playback" to={`/videos/${c.id}`} />
                    </>
                  )}
                  {/* Staf saja sejak 8 Sep 2026, mengikuti menu Logs yang juga
                      pindah ke sana. Membiarkannya di sini untuk viewer berarti
                      tombol yang mengantar ke halaman yang menolaknya — pintu
                      yang kelihatan bisa dibuka lalu ternyata tidak. */}
                  {staf && !terkunci(c) && (
                    <TautanIkon label={t('View logs')} nama="logs" to={`/logs?camera_id=${c.id}`} />
                  )}

                  {/* Add to dashboard (§F9, 23 Agu 2026). Terbuka untuk semua
                      peran, sama seperti halaman Custom Dashboards sendiri —
                      justru pemakai biasa yang paling memerlukannya. */}
                  {/* Monitor milik sendiri: pemilik kamera privat boleh, pengelola tidak. */}
                  {!terkunci(c) && (
                  <MenuIkon
                    label={t('Add to dashboard')}
                    nama="addToDashboard"
                    terbuka={panel?.id === c.id && panel.jenis === 'dash'}
                    onToggle={() => togglePanel(c.id, 'dash')}
                    onTutup={tutupPanel}
                  >
                    {dashboards.length === 0 ? (
                      <p className="px-2.5 py-2 text-sm text-muted">
                        {t('No custom dashboards yet.')}
                      </p>
                    ) : (
                      dashboards.map((d) => {
                        const berisi = d.cameraIds.includes(c.id);
                        return (
                          <button
                            data-item
                            key={d.id}
                            type="button"
                            // Panel TIDAK ditutup sesudah dipilih: kamera yang
                            // sama sering masuk ke dua-tiga dashboard sekaligus,
                            // dan menutupnya berarti membuka lagi tiap kali.
                            onClick={() => void toggleDiDashboard(d, c.id)}
                            aria-pressed={berisi}
                            disabled={dashBusy}
                            className={`${ITEM_MENU} disabled:opacity-50`}
                          >
                            {/* Kotak centang, bukan warna saja (§G3): ada dan
                                tidak ada harus terbaca tanpa membedakan warna. */}
                            <span
                              aria-hidden="true"
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                berisi ? 'border-accent bg-accent text-accent-contrast' : 'border-line'
                              }`}
                            >
                              {berisi && (
                                <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="m5 12.5 4.5 4.5L19 7" />
                                </svg>
                              )}
                            </span>
                            <span className="truncate">{d.name}</span>
                          </button>
                        );
                      })
                    )}

                    <hr className="my-1 border-line" />
                    <button
                      data-item={dashboards.length === 0 ? true : undefined}
                      type="button"
                      disabled={dashBusy}
                      onClick={() => {
                        tutupPanel();
                        void dashboardBaru(c);
                      }}
                      className={`${ITEM_MENU} disabled:opacity-50`}
                    >
                      <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center">
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </span>
                      {t('New dashboard…')}
                    </button>
                  </MenuIkon>
                  )}

                  {staf && (
                    <>
                      <MenuIkon
                        label={t('More options')}
                        nama="more"
                        terbuka={panel?.id === c.id && panel.jenis === 'more'}
                        onToggle={() => togglePanel(c.id, 'more')}
                        onTutup={tutupPanel}
                      >
                        {/* Uji koneksi pindah ke sini 23 Agu 2026, bersamaan
                            dengan masuknya "Add to dashboard" ke baris terbuka.
                            Barisnya cuma muat lima ikon pada kartu `Medium`,
                            dan yang keenam membuatnya patah jadi dua baris di
                            SETIAP kartu — persis penyakit yang dulu disembuhkan
                            dengan mengganti tombol teks jadi ikon.

                            Yang mengalah uji koneksi, bukan yang lain: ia
                            diagnostik yang dipakai saat memasang kamera dan
                            saat ada yang rusak, sementara "Add to dashboard"
                            dipakai orang yang sedang menyusun layar pantaunya.
                            Aturannya tetap sama — yang terbuka adalah yang
                            paling sering dipakai.

                            Panel bersarang dihindari: dua barisnya ditulis
                            datar di sini, bukan sebagai menu di dalam menu. */}
                        <button
                          data-item
                          type="button"
                          className={ITEM_MENU}
                          onClick={() => {
                            tutupPanel();
                            void test(c, 'main');
                          }}
                        >
                          {IKON_AKSI.test} {t('Test main stream')}
                        </button>
                        {/* Kamera tanpa substream tidak diberi baris yang tidak
                            bisa dijawab apa pun. */}
                        {c.subPath && (
                          <button
                            data-item
                            type="button"
                            className={ITEM_MENU}
                            onClick={() => {
                              tutupPanel();
                              void test(c, 'sub');
                            }}
                          >
                            {IKON_AKSI.stream} {t('Test sub stream')}
                          </button>
                        )}
                        <hr className="my-1 border-line" />
                        {/* Pemeliharaan (§D10.2). Menandai kamera menghentikan
                            recorder-nya dan membatalkan backoff-nya: kamera
                            yang sedang dibongkar orang menghasilkan sesi RTSP
                            putus-nyambung, dan supervisor yang terus mencoba
                            bukan cuma sia-sia — ia mengisi log dengan ratusan
                            baris `restart` tepat pada rentang waktu yang nanti
                            paling ingin dibaca orang. */}
                        <button
                          data-item
                          type="button"
                          className={ITEM_MENU}
                          onClick={() => {
                            tutupPanel();
                            const catatan = c.maintenanceSince
                              ? null
                              : (window.prompt(t('What is being done to this camera?')) ?? '');
                            if (!c.maintenanceSince && catatan === null) return;
                            void setCameraMaintenance(c.id, catatan)
                              .then(load)
                              .catch((e) => setError((e as Error).message));
                          }}
                        >
                          {IKON_AKSI.test}{' '}
                          {c.maintenanceSince ? t('End maintenance') : t('Mark as maintenance')}
                        </button>
                        <button
                          data-item
                          type="button"
                          className={ITEM_MENU}
                          onClick={() => {
                            tutupPanel();
                            setAkanDiganti(c);
                          }}
                        >
                          {IKON_AKSI.stream} {t('Replace camera…')}
                        </button>
                        <hr className="my-1 border-line" />
                        <button
                          data-item
                          type="button"
                          className={ITEM_MENU}
                          onClick={() => {
                            tutupPanel();
                            setEditing(c);
                          }}
                        >
                          {IKON_AKSI.edit} {t('Edit')}
                        </button>
                        {/* Web UI kamera, bukan halaman Lensa — karena itu ia
                            `rel="noreferrer"` dan membuka tab baru: alamat
                            Lensa tidak perlu ikut ke perangkat pihak ketiga,
                            dan menggantikan tab yang sedang dipakai memantau
                            adalah hal terakhir yang diinginkan orang. */}
                        <a
                          data-item
                          href={c.webUrl ?? undefined}
                          target="_blank"
                          rel="noreferrer noopener"
                          className={ITEM_MENU}
                          onClick={tutupPanel}
                          title={`Open ${c.webUrl} in a new tab`}
                        >
                          {IKON_AKSI.gear} {t('Camera settings')}
                        </a>
                        {/* Hapus hanya untuk Administrator (§F8). Operator boleh
                            menambah dan menata; mencabut adalah satu-satunya
                            hal yang tidak diberikan kepadanya, dan pembedanya
                            sengaja cuma satu supaya gampang dijelaskan.
                            Disembunyikan, bukan di-disable: tombol mati yang
                            tidak pernah bisa dipakai hanya mengajari orang
                            bahwa ada sesuatu yang tidak boleh mereka sentuh
                            (§G9). Server tetap yang menjaganya. */}
                        {admin && (
                          <button
                            data-item
                            type="button"
                            className={`${ITEM_MENU} text-red-600 hover:bg-red-600/10`}
                            onClick={() => {
                              tutupPanel();
                              setAkanDihapus(c);
                            }}
                          >
                            {IKON_AKSI.trash} {t('Delete')}
                          </button>
                        )}
                      </MenuIkon>
                    </>
                  )}
                </div>
                )}

                {rinci && uji === 'running' && (
                  <p role="status" className="mt-2 text-xs text-muted">
                    {t('Testing connection…')}
                  </p>
                )}
                {rinci && uji && uji !== 'running' && (
                  <p role="status" className="mt-2 text-xs">
                    {uji.ok ? (
                      <>
                        {t('Connected')} — {uji.video?.codec}
                        {uji.video?.width ? ` ${uji.video.width}×${uji.video.height}` : ''}
                        {uji.video?.fps ? ` ${t('at')} ${uji.video.fps} fps` : ''}
                        {uji.hasAudio ? `, ${t('with audio')}` : ''}
                        {uji.video?.isHevc && (
                          <span className="text-amber-500">
                            {' '}
                            — {t('H.265 counts double against the streaming budget')}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-amber-500">
                        {t('Failed')} — {uji.message}
                      </span>
                    )}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Menempel di kaki halaman, dan hanya muncul saat ada yang terpilih —
          bilah aksi yang selalu ada memakan tinggi kartu terakhir setiap hari
          demi tindakan yang dipakai sesekali (§G7 no.1). */}
      {staf && (
        <CameraBulkBar
          terpilih={terpilihAktif}
          totalTerlihat={terlihat.length}
          tags={tags}
          templates={templates}
          admin={admin}
          onPilihSemua={() => setTerpilih(new Set(terlihat.map((c) => c.id)))}
          onBersihkan={() => setTerpilih(new Set())}
          onSelesai={load}
        />
      )}

      {layarPenuh && (
        <FullscreenViewer
          camera={layarPenuh}
          onClose={() => setLayarPenuh(null)}
          {...(() => {
            // Menjelajah daftar yang sedang terlihat — hasil pencarian dan
            // urutan yang dipilih, bukan urutan mentah dari server. Yang
            // dilihat orang di halaman itulah urutan yang dia harapkan saat
            // menekan panah (23 Agu 2026).
            // Kamera terkunci dilewati — membukanya berarti meminta stream yang ditolak.
            const daftar = terlihat.filter((c) => !terkunci(c));
            const i = daftar.findIndex((c) => c.id === layarPenuh.id);
            const geser = (arah: 1 | -1) => {
              const n = (i + arah + daftar.length) % daftar.length;
              const cam = daftar[n];
              if (cam) setLayarPenuh(cam);
            };
            return daftar.length > 1 && i >= 0
              ? {
                  onPrev: () => geser(-1),
                  onNext: () => geser(1),
                  posisi: { index: i, total: daftar.length },
                }
              : {};
          })()}
        />
      )}

      {akanDihapus && (
        <DialogHapus
          nama={akanDihapus.name}
          judul={t('Delete camera {nama}', { nama: akanDihapus.name })}
          keterangan={
            <>
              <strong className="text-[color:var(--color-text)]">{akanDihapus.name}</strong>{' '}
              {t(
                'and its entire recording index will be permanently deleted. Recorded files already on disk are removed with it.',
              )}
            </>
          }
          onBatal={() => setAkanDihapus(null)}
          onHapus={(konfirmasi) => remove(akanDihapus, konfirmasi)}
        />
      )}

      <Pager
        page={paging.page}
        pageCount={paging.pageCount}
        start={paging.start}
        shown={paging.slice.length}
        total={paging.total}
        noun="cameras"
        onPage={paging.setPage}
      />
    </div>
  );
}
